/**
 * CatchImage — universal renderer used by every surface that shows a user-
 * submitted catch photo. Wraps `resolveCatchImage` and falls back to a
 * branded Carp Cast placeholder when the image is missing OR fails to load.
 *
 * Variants:
 *   • default (aspectRatio="video") — full feed/card style
 *   • aspectRatio="square" — small thumbnails (dashboard, league rows)
 *   • aspectRatio="auto" — caller controls dimensions
 *
 * Behaviour:
 *   1) Resolve URL via `resolveCatchImage(row)` (data URI / absolute / /api
 *      prefix / null).
 *   2) If null → render placeholder immediately.
 *   3) If the resolved URL 404s or errors → swap to placeholder via onError.
 *
 * Placeholder = dark gradient + lucide Fish icon in Carp Cast gold. Matches
 * the rest of the app and is *deliberately* not a browser default.
 */
import { useState } from 'react';
import { Fish } from 'lucide-react';
import { resolveCatchImage } from '../lib/catchImage';

const ASPECTS = {
  video:  'aspect-video',
  square: 'aspect-square',
  auto:   '',
};

const CatchImage = ({
  row,
  alt = 'Catch photo',
  aspectRatio = 'video',
  className = '',
  imgClassName = 'w-full h-full object-cover',
  rounded = false,
  testId,
}) => {
  const initialSrc = resolveCatchImage(row);
  const [errored, setErrored] = useState(false);
  const showImage = initialSrc && !errored;

  const wrapperClass = [
    ASPECTS[aspectRatio] || '',
    'bg-[#141714] relative overflow-hidden flex items-center justify-center',
    rounded ? 'rounded' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClass} data-testid={testId || 'catch-image'}>
      {showImage ? (
        <img
          src={initialSrc}
          alt={alt}
          className={imgClassName}
          loading="lazy"
          onError={() => setErrored(true)}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-gradient-to-br from-[#1a1f1a] to-[#0b0d0b]">
          <Fish className="w-6 h-6 text-[#c8b273]/80" strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#c8b273]/60 font-bold">
            No photo
          </span>
        </div>
      )}
    </div>
  );
};

export default CatchImage;
